import { useMemo } from 'react'
import { resolveUploadsUrl } from '@/lib/uploads'
import { statusLabel } from '@/lib/ticket-display'
import {
  REPLY_PANEL_H_MAX,
  TICKET_DETAIL_STATUS_OPTIONS,
  mergeCustomerIdentifiers,
} from '@/components/tickets/ticket-detail-page-helpers'
import type {
  CustomerRow,
  MacroRow,
  ProjectCustomerFieldRow,
  ProjectDetailResponse,
  ProjectTagRow,
  ProjectTemplateRow,
  ProjectUserIdentifierRow,
  TicketDetailMessage,
  TicketDetailTicket,
  TicketProductReviewStatusResponse,
} from '@/types/api'

type UseTicketDetailViewModelParams = {
  customer: CustomerRow | null
  customerIdentifierRows: ProjectUserIdentifierRow[]
  filesCount: number
  isPhoneLayout: boolean
  isTicketWideLayout: boolean
  macros: MacroRow[]
  pendingInstallersCount: number
  productReviewStatus: TicketProductReviewStatusResponse | null
  projectCustomerFields: ProjectCustomerFieldRow[]
  projectDetail: ProjectDetailResponse | null
  quotedMessage: TicketDetailMessage | null
  replyFavorites: { template_ids: string[]; macro_ids: string[] }
  replyTarget: 'customer' | 'internal'
  tags: ProjectTagRow[]
  templates: ProjectTemplateRow[]
  ticket: TicketDetailTicket | null
}

export function useTicketDetailViewModel({
  customer,
  customerIdentifierRows,
  filesCount,
  isPhoneLayout,
  isTicketWideLayout,
  macros,
  pendingInstallersCount,
  productReviewStatus,
  projectCustomerFields,
  projectDetail,
  quotedMessage,
  replyFavorites,
  replyTarget,
  tags,
  templates,
  ticket,
}: UseTicketDetailViewModelParams) {
  const widgetEmailWait = useMemo(() => {
    const customFields = (ticket?.custom_fields || null) as Record<string, unknown> | null
    if (!customFields) return { active: false, replyTo: '' }
    const waiting = customFields.widget_waiting_operator_email
    const active = waiting === true || waiting === 'true' || waiting === 1 || waiting === '1'
    const replyTo = String(customFields.widget_reply_to_email || ticket?.customer_email || '').trim()
    return { active, replyTo }
  }, [ticket?.custom_fields, ticket?.customer_email])

  const displayIdentifiers = useMemo(
    () => mergeCustomerIdentifiers(ticket, customer, customerIdentifierRows),
    [customer, customerIdentifierRows, ticket],
  )

  const sidebarEmail = useMemo(() => {
    const identifierEmail = displayIdentifiers
      .find((identifier) => identifier.identifier_type === 'email')
      ?.identifier_value?.trim()
    return identifierEmail || ''
  }, [displayIdentifiers])

  const pinnedTemplates = useMemo(
    () =>
      (replyFavorites.template_ids || [])
        .map((id) => templates.find((template) => template.id === id))
        .filter((template): template is ProjectTemplateRow => Boolean(template)),
    [replyFavorites.template_ids, templates],
  )

  const pinnedMacros = useMemo(
    () =>
      (replyFavorites.macro_ids || [])
        .map((id) => macros.find((macro) => macro.id === id))
        .filter((macro): macro is MacroRow => Boolean(macro)),
    [replyFavorites.macro_ids, macros],
  )

  const replyPanelMinHeight = useMemo(() => {
    const base = isPhoneLayout ? 120 : !isTicketWideLayout ? 152 : 216
    let min = base
    if (quotedMessage) min += isPhoneLayout ? 56 : !isTicketWideLayout ? 72 : 104
    if (ticket?.status === 'resolved') min += isPhoneLayout ? 36 : !isTicketWideLayout ? 44 : 52
    if (pinnedTemplates.length > 0 || pinnedMacros.length > 0) {
      min += isPhoneLayout ? 32 : !isTicketWideLayout ? 40 : 48
    }
    if (filesCount > 0 || pendingInstallersCount > 0) {
      min += isPhoneLayout ? 32 : !isTicketWideLayout ? 38 : 44
    }
    if (replyTarget === 'internal') min += isPhoneLayout ? 32 : !isTicketWideLayout ? 38 : 44
    const floor = isPhoneLayout ? 96 : !isTicketWideLayout ? 112 : 196
    const ceiling = isPhoneLayout ? 240 : !isTicketWideLayout ? 300 : REPLY_PANEL_H_MAX
    return Math.min(ceiling, Math.max(floor, min))
  }, [
    filesCount,
    isPhoneLayout,
    isTicketWideLayout,
    pendingInstallersCount,
    pinnedMacros.length,
    pinnedTemplates.length,
    quotedMessage,
    replyTarget,
    ticket?.status,
  ])

  const ticketStatusSelectData = useMemo(() => {
    const status = ticket?.status
    if (!status) return TICKET_DETAIL_STATUS_OPTIONS
    if (
      (status === 'on_hold' || status === 'spam') &&
      !TICKET_DETAIL_STATUS_OPTIONS.some((option) => option.value === status)
    ) {
      return [...TICKET_DETAIL_STATUS_OPTIONS, { value: status, label: statusLabel(status) }]
    }
    return TICKET_DETAIL_STATUS_OPTIONS
  }, [ticket?.status])

  const tagsToAdd = useMemo(
    () => tags.filter((tag) => tag.is_active !== false && !(ticket?.tag_ids || []).includes(tag.id)),
    [tags, ticket?.tag_ids],
  )

  const customerHeadingName = useMemo(() => {
    const fromProfile = [customer?.first_name, customer?.last_name].filter(Boolean).join(' ').trim()
    return fromProfile || ticket?.customer_name || '—'
  }, [customer?.first_name, customer?.last_name, ticket?.customer_name])

  const avatarResolvedSrc = useMemo(
    () => (customer?.avatar_url ? resolveUploadsUrl(customer.avatar_url) : undefined),
    [customer?.avatar_url],
  )

  const sortedProjectFields = useMemo(
    () =>
      [...projectCustomerFields]
        .filter((field) => field.is_active)
        .sort((left, right) => left.display_order - right.display_order),
    [projectCustomerFields],
  )

  const enableReview = Boolean(projectDetail?.enable_review_request)
  const enableProductReview = Boolean(projectDetail?.enable_product_review_request)
  const showMyWorkReviewBlock = enableReview && ticket?.status !== 'closed' && !!ticket?.customer_id
  const showProductReviewBlock = enableProductReview && !!ticket?.customer_id
  const isEmailChannel = String(ticket?.channel_type ?? '').trim().toLowerCase() === 'email'
  const canPostProductReview = Boolean(
    ticket && ticket.status !== 'closed' && ticket.status !== 'spam' && !productReviewStatus?.requested,
  )
  const hasOtherProductRequests = (productReviewStatus?.other_requests?.length ?? 0) > 0

  return {
    avatarResolvedSrc,
    canPostProductReview,
    customerHeadingName,
    displayIdentifiers,
    hasOtherProductRequests,
    isEmailChannel,
    pinnedMacros,
    pinnedTemplates,
    replyPanelMinHeight,
    showMyWorkReviewBlock,
    showProductReviewBlock,
    sidebarEmail,
    sortedProjectFields,
    tagsToAdd,
    ticketStatusSelectData,
    widgetEmailWait,
  }
}
